// ===========================================
// LANGUAGE GUARD - Language Validation
// ===========================================

import { type ReactNode, useEffect } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { appConfig, isLanguageSupported } from '@/config';
import { getLanguageDirection } from '@/config/i18n.config';
import { setLanguage } from '@/store/slices/preferencesSlice';
import type { Language } from '@/types';
import i18n from '@/i18n';

interface LanguageGuardProps {
  children: ReactNode;
}

/**
 * Guard component for language validation on /:lang/* routes.
 * - Validates the :lang param is a supported language code
 * - Syncs language into i18next, Redux store, and document attributes
 * - Redirects to /{defaultLang}/path if the language is unknown
 */
export function LanguageGuard({ children }: LanguageGuardProps) {
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!lang || !isLanguageSupported(lang)) return;

    const validLang = lang as Language;

    // Sync i18next
    if (i18n.language !== validLang) {
      i18n.changeLanguage(validLang);
    }

    // Sync Redux store
    dispatch(setLanguage(validLang));

    // Sync document attributes for RTL support
    document.documentElement.lang = validLang;
    document.documentElement.dir = getLanguageDirection(validLang);
  }, [lang, dispatch]);

  // Unknown language → redirect to default, preserving sub-path
  if (!lang || !isLanguageSupported(lang)) {
    const pathWithoutLang = location.pathname.replace(/^\/[^/]+/, '');
    const newPath = `/${appConfig.defaultLanguage}${pathWithoutLang}${location.search}`;
    return <Navigate to={newPath} replace />;
  }

  return <>{children}</>;
}

export default LanguageGuard;
